<template>
  <v-sheet v-for="(item, index) in items" :key="index">
    <v-row dense>
      <v-col cols="10">
        <v-text-field
          v-model="item.value"
          :label="$t('labels.value')"
          variant="outlined"
          density="compact"
          :rules="getValueRules(item)"
          @focus="item.touchedValue = true"
          @dragover.prevent
          @drop="onDrop($event, item, 'value')"
        ></v-text-field>
      </v-col>
      <v-col cols="2" text-center>
        <v-btn
          v-if="index < items.length - 1"
          class="mt-n1"
          variant="plain"
          icon="mdi-delete"
          color="secondary"
          @click="removeItem(index)"
        ></v-btn>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script>
import { inject, toRefs, reactive, watch, computed } from "vue";
import { drop, checkDrop } from "@/mixins/common-utils.js";
import { useI18n } from "vue-i18n";

export default {
  props: ["data", "dynamicParams"],
  setup(props) {
    const valid = inject("valid");// Do not force valid to false at mount
    const { t } = useI18n();
    const formData = inject("formData");
    const valuesType = computed(() => props.dynamicParams.valuesType);

    // Initialize parameters if they don't exist
    if (Object.keys(formData).length <= 1) {
      formData.parameters = [{ value: "", touchedValue: false }];
    }

    // Reactive state for the component
    const state = reactive({
      items:
        formData.parameters.length > 0
          ? revertToItems(formData.parameters)
          : [{ value: "", touchedValue: false }],
    });
    const { items } = toRefs(state);

    // Watch for changes in items and add a new empty row if needed
    watch(
      items,
      (newItems) => {
        const lastItem = newItems[newItems.length - 1];
        if (lastItem.value) {
          newItems.push({
            value: "",
            touchedValue: false,
          });
        }
        const staticParams = formData.parameters.filter(
          (p) => !p.name || !p.name.startsWith("__item_"),
        );
        formData.parameters = [...staticParams, ...breakAndEnrichItem(newItems)];
        resetTouchedState(newItems);
        validateItems(newItems);
      },
      { deep: true },
    );

    // Reset touched state if there is only one empty row
    const resetTouchedState = (items) => {
      if (items.length === 1 && !items[0].value) {
        items[0].touchedValue = false;
      }
    };

    // Validate items based on the new rules
    const validateItems = (items) => {
      const nonEmptyItems = items.filter((item) => item.value);
      const emptyItems = items.filter((item) => !item.value);
      valid.value = nonEmptyItems.length > 0 && emptyItems.length <= 1;
    };

    // Check if there is only one item
    const isSingleItem = () => items.value.length === 1;

    // Check if there are multiple empty items
    const hasMultipleEmptyItems = () =>
      items.value.filter((item) => !item.value).length > 1;

    // Computed properties for validation rules
    const getValueRules = () => [
      (v) => {
        if (isSingleItem() && !v) {
          return t("strings.value-is-required");
        }
        if (!isSingleItem() && hasMultipleEmptyItems() && !v) {
          return t("strings.value-is-required");
        }
        return true;
      },
      (v) => {
        if (!isSingleItem() && v == "not valid") {
          return t("strings.value-must-be-type") + " " + valuesType.value;
        }
        if (hasMultipleEmptyItems() && v == "not valid") {
          return t("strings.value-must-be-type") + " " + valuesType.value;
        }
        return true;
      },
    ];

    // Handle drop event for drag-and-drop
    const onDrop = (event, item, targetName) => {
      event.stopPropagation();
      try {
        const isValidDrop = checkDrop(event) === valuesType.value;

        if (isValidDrop) {
          drop(event, targetName, item);
          valid.value = true;
        } else {
          item[targetName] = t("strings.not-valid");
          valid.value = false;
        }
      } catch (error) {
        console.error("Error handling drop event: ", error);
      }
    };

    // Remove a value pair
    const removeItem = (index) => {
      try {
        items.value.splice(index, 1);
        const staticParams = formData.parameters.filter(
          (p) => !p.name || !p.name.startsWith("__item_"),
        );
        formData.parameters = [...staticParams, ...breakAndEnrichItem(items.value)];
        resetTouchedState(items.value);
        validateItems(items.value);
      } catch (error) {
        console.error("Error removing item: ", error);
      }
    };

    // Split an array of objects with value pairs
    // into an array of object pairs with a single property value
    function breakAndEnrichItem() {
      const createObject = (name, property) => ({
        name,
        property,
        property2: "undefined",
        operator: "undefined",
        sourceexp: true,
        bind: true,
        custom: true,
        value: "", // Initialize value property 
      });
      valid.value = true;
      return items.value.flatMap((item, index) => {
        const objects = [];
        if (item.value) {
          objects.push(createObject("__item_" + index, item.value));
        }
        return objects;
      });
    }

    // Transforms an array of objects with a single property into an array of objects with value pairs
    function revertToItems(parameters) {
      let items = [];
      let currentItem = {
        value: "",
        touchedValue: false,
      };

      parameters.forEach((param) => {
        if (param.name.startsWith("__item_")) {
          currentItem.value = param.property;
        }

        // If value is set, push the current item and reset it
        if (currentItem.value) {
          items.push({ ...currentItem });
          currentItem = {
            value: "",
            touchedValue: false,
          };
        }
      });

      // Add an empty value pair at the end
      items.push({
        value: "",
        touchedValue: false,
      });
      return items;
    }

    return {
      valuesType,
      onDrop,
      formData,
      items,
      removeItem,
      getValueRules,
      valid,
    };
  },
};
</script>
